import React, { useEffect, useState } from "react";
import { getPotwProfile, updatePotwProfile } from "../api/client";
import DocumentsPanel from "./Documents/DocumentsPanel";
import NpdesPermits from "./NpdesPermits";

/**
 * Admin editor for the POTW identity (deploy-per-customer). These values appear
 * as the letterhead / contact / signatory on enforcement letters and reports.
 */
type Profile = {
  id?: number;
  name: string;
  address: string;
  phone: string;
  email: string;
  coordinator_name: string;
  signatory_name: string;
  signatory_title: string;
  iu_permit_prefix: string;
  // For the SCDES Pretreatment Performance Report.
  mailing_address: string;
  county: string;
  wwtp_name: string;
  wwtp_site_address: string;
  design_flow_mgd: string;
  local_limits_parameters: string;
  local_limits_adoption_date: string;
  local_limits_evaluation_date: string;
};

const EMPTY: Profile = {
  name: "", address: "", phone: "", email: "",
  coordinator_name: "", signatory_name: "", signatory_title: "",
  iu_permit_prefix: "",
  mailing_address: "", county: "", wwtp_name: "", wwtp_site_address: "",
  design_flow_mgd: "", local_limits_parameters: "",
  local_limits_adoption_date: "", local_limits_evaluation_date: "",
};

// The API sends null for an unset date or number; the inputs want "".
const fromApi = (d: any): Profile => {
  const p: any = { ...EMPTY };
  for (const k of Object.keys(EMPTY)) if (d[k] !== null && d[k] !== undefined) p[k] = String(d[k]);
  return { ...p, id: d.id };
};

type FieldDef = { key: keyof Profile; label: string; hint?: string; type?: "date" | "number" | "textarea" };

const FIELDS: FieldDef[] = [
  { key: "name",             label: "POTW / Utility name" },
  { key: "address",          label: "Address" },
  { key: "phone",            label: "Phone" },
  { key: "email",            label: "Email" },
  { key: "coordinator_name", label: "Pretreatment coordinator" },
  { key: "signatory_name",   label: "Signing official — name" },
  { key: "signatory_title",  label: "Signing official — title" },
  { key: "iu_permit_prefix", label: "IU permit-number prefix",
    hint: "State-assigned designator (e.g. SCIUAC). New industrial users are numbered SCIUAC001, SCIUAC002, …" },
];

// Asked on the SCDES Pretreatment Performance Report's POTW section.
const PPS_FIELDS: FieldDef[] = [
  { key: "mailing_address",   label: "Mailing address", hint: "If different from the address above (e.g. a PO Box)." },
  { key: "county",            label: "County" },
  { key: "wwtp_name",         label: "Treatment plant name", hint: "As on the NPDES permit, e.g. \"Riverside WWTF\"." },
  { key: "wwtp_site_address", label: "Treatment plant site address" },
  { key: "design_flow_mgd",   label: "Design flow (MGD)", type: "number",
    hint: "The DES-approved design capacity of the plant." },
  { key: "local_limits_adoption_date",   label: "Local limits — most recent adoption",   type: "date" },
  { key: "local_limits_evaluation_date", label: "Local limits — most recent evaluation", type: "date" },
  { key: "local_limits_parameters", label: "Local limits used by the POTW", type: "textarea",
    hint: "One per line, worded as on the SCDES list — e.g. \"Copper, total [as Cu]\", \"BOD, 5-day [20 deg. C]\", \"pH\"." },
];

// readOnly: someone other than the program owner (only the owner changes these).
export default function PotwProfileForm({ readOnly = false, ownerName = "" }: { readOnly?: boolean; ownerName?: string }) {
  const [profile, setProfile] = useState<Profile>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState("");

  useEffect(() => {
    getPotwProfile()
      .then(r => setProfile(fromApi(r.data)))
      .catch(() => setStatus("Could not load the POTW profile."))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: keyof Profile, value: string) =>
    setProfile(p => ({ ...p, [key]: value }));

  const save = () => {
    setSaving(true);
    setStatus("");
    updatePotwProfile(profile)
      .then(r => { setProfile(fromApi(r.data)); setStatus("Saved."); })
      .catch(e => setStatus(e.response?.data?.error ?? "Save failed."))
      .finally(() => setSaving(false));
  };

  const field = (f: FieldDef) => (
    <label key={f.key} style={s.field}>
      <span style={s.label}>{f.label}</span>
      {f.type === "textarea" ? (
        <textarea style={s.input} rows={6} value={profile[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} />
      ) : (
        <input
          style={s.input}
          type={f.type ?? "text"}
          step={f.type === "number" ? "any" : undefined}
          min={f.type === "number" ? 0 : undefined}
          value={profile[f.key] ?? ""}
          onChange={e => set(f.key, e.target.value)}
        />
      )}
      {f.hint && <span style={s.hint}>{f.hint}</span>}
    </label>
  );

  if (loading) return <div style={s.muted}>Loading…</div>;

  return (
    <div style={s.wrap}>
      <h2 style={s.h2}>POTW Profile</h2>
      <p style={s.muted}>
        Used as the letterhead, contact, and signatory on enforcement letters and reports.
      </p>
      {readOnly && <p style={s.ownerNote}>Only the program owner{ownerName ? ` (${ownerName})` : ""} can change these settings.</p>}
      <fieldset disabled={readOnly} style={{border:"none", padding:0, margin:0, minWidth:0}}>
      {FIELDS.map(field)}
      <h3 style={s.h3}>NPDES permits</h3>
      <p style={s.muted}>
        The POTW's own NPDES permit, renewed about every five years. Add a renewal as a new line; the old one
        stays as history and each report uses the permit in force during its period. A reminder appears from
        180 days before the current permit expires, when the renewal application is due.
      </p>
      <NpdesPermits canEdit={!readOnly} />
      <h3 style={s.h3}>SCDES Pretreatment Performance Report</h3>
      <p style={s.muted}>Printed in the POTW section of the semi-annual / annual report.</p>
      {PPS_FIELDS.map(field)}
      </fieldset>
      {!readOnly && (
        <div style={s.actions}>
          <button style={s.save} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save profile"}
          </button>
          {status && <span style={s.status}>{status}</span>}
        </div>
      )}

      {/* Authorization & program documents on file for this POTW — e.g. the signed
          SC ePermitting Certifier Agreement (see docs/SCDES_SUBMISSION_PATH.md). */}
      {profile.id != null && (
        <div style={s.docs}>
          <DocumentsPanel
            entityType="potw_profile"
            entityId={profile.id}
            category="certifier_agreement"
            title="Authorization & program documents"
          />
          <p style={s.hint}>
            Store signed regulator authorizations here — e.g. the SC ePermitting Certifier
            Agreement that lets the coordinator submit the pretreatment report.
          </p>
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 560 },
  h2: { fontSize: 20, margin: "0 0 4px" },
  h3: { fontSize: 16, margin: "24px 0 2px", paddingTop: 16, borderTop: "1px solid #edf2f7" },
  muted: { color: "#718096", fontSize: 13, marginTop: 0 },
  field: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 },
  label: { fontSize: 13, fontWeight: 600, color: "#4a5568" },
  hint: { fontSize: 12, color: "#718096" },
  input: { padding: "8px 10px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: 14 },
  actions: { display: "flex", alignItems: "center", gap: 12, marginTop: 8 },
  docs: { marginTop: 24, paddingTop: 16, borderTop: "1px solid #edf2f7" },
  save: { padding: "8px 16px", background: "#2b6cb0", color: "#fff", border: "none",
          borderRadius: 6, fontSize: 14, cursor: "pointer" },
  status: { fontSize: 13, color: "#2f855a" },
  ownerNote: { fontSize: 13, color: "#975a16", background: "#fffbeb", border: "1px solid #f6e05e",
               borderRadius: 6, padding: "8px 12px", margin: "0 0 12px" },
};
